import type { InvoiceListFilters, State } from '../../api/invoices.js';

// REQ-FE-029e — Saved-view pill tabs at the top of the invoice list.
// Each tab maps to a curated state-filter combination that matches the
// mockup's primary affordance (`03-invoice-list.png`).
//
// Counts are deferred: the contract has no `/api/invoices/counts` endpoint
// today, and counting client-side from a single page misrepresents the
// real total. Showing live counts would need either a BE aggregator or a
// client-side fan-out (one query per view) — both are follow-ups. The
// current implementation surfaces the labels with no count badge; the
// `count` prop is plumbed through so the wire-up is trivial later.

export type ViewKey = 'all' | 'todo' | 'urgent' | 'disputed' | 'approved';

const VIEW_LABEL_FR: Record<ViewKey, string> = {
  all: 'Toutes',
  todo: 'À traiter',
  urgent: 'Urgentes',
  disputed: 'Litiges',
  approved: 'Approuvées',
};

// State buckets per view. "Urgent" today reuses the À-traiter set
// because the contract has no overdue-only filter at the list endpoint;
// the overdue overlay is computed client-side via `dueDate`. Tighter
// urgency filtering is a deferred follow-up.
const VIEW_STATES: Record<ViewKey, State[]> = {
  all: [],
  todo: ['RECEIVED', 'PROCESSING', 'AVAILABLE'],
  urgent: ['RECEIVED', 'PROCESSING', 'AVAILABLE'],
  disputed: [],
  approved: ['ACKNOWLEDGED'],
};

// Match the currently-applied filters back to a view key. When no view
// matches exactly, default to "all" so the toolbar always has one
// active pill. Empty filter set always resolves to "all" — both `all`
// and `disputed` have empty state lists today (disputed has no state
// representation in the contract yet; UI surface needs the tab but the
// list query can't filter on it server-side), so `all` wins by
// precedence to avoid the toolbar showing `disputed` when the user
// hasn't selected anything.
export function inferActiveView(filters: InvoiceListFilters): ViewKey {
  if (filters.states.length === 0) return 'all';
  const s = [...filters.states].sort().join(',');
  for (const v of Object.keys(VIEW_STATES) as ViewKey[]) {
    if (v === 'all' || v === 'disputed') continue;
    if ([...VIEW_STATES[v]].sort().join(',') === s) return v;
  }
  return 'all';
}

interface Props {
  filters: InvoiceListFilters;
  onChange: (next: InvoiceListFilters) => void;
  // Map of view → count badge. When omitted (default) the badges are
  // hidden. Wire this once a counts source is available.
  counts?: Partial<Record<ViewKey, number>>;
}

export function InvoiceFilterPills({ filters, onChange, counts }: Props) {
  const activeView = inferActiveView(filters);
  function selectView(v: ViewKey) {
    onChange({ ...filters, states: [...VIEW_STATES[v]] });
  }
  return (
    <div
      className="invoice-filter-pills"
      role="tablist"
      aria-label="Vue de la liste"
      data-testid="invoice-filter-pills"
    >
      {(Object.keys(VIEW_LABEL_FR) as ViewKey[]).map((v) => {
        const isActive = activeView === v;
        const count = counts?.[v];
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`invoice-filter-pill${isActive ? ' is-active' : ''}`}
            onClick={() => selectView(v)}
            data-testid={`invoice-view-${v}`}
          >
            <span>{VIEW_LABEL_FR[v]}</span>
            {typeof count === 'number' ? (
              <span className="invoice-filter-pill__count">{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
